import { useEffect, useMemo, useRef, RefObject } from 'react'
import { Typography, Box } from '@mui/material'
import {
  StockProjectionExceptionTableData,
  StockProjectionExceptionTableProps,
} from './StockProjectionException.types'
import {
  GridFilterModel,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium'
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'

type ColorClassType = 'red' | 'green' | 'amber'

export const SPETable = ({
  data,
  handleRowClick,
  handleSelectedGraphsChange,
  isLoading,
  selectedSkus,
  selectedSkuName,
  hoveredGraphSite,
}: StockProjectionExceptionTableProps) => {
  const tableApiRef = useRef<RefObject<GridApiPremium> | null>(null)

  useEffect(() => {
    if (data.length > 0 && selectedSkus.length === 0) {
      setTimeout(() => {
        handleRowClick(data)
        tableApiRef.current?.current?.setRowSelectionModel({
          type: 'exclude',
          ids: new Set(),
        })
      }, 1000)
    }
  }, [data])

  const handleRowClickWrapper = (params: any) => {
    const selectedRow = data.find(
      (row: StockProjectionExceptionTableData) => row.index === params.id
    )
    if (selectedRow) {
      handleRowClick([selectedRow])
    }
  }

  const handleCheckboxSelectionChange = (
    selectionModel: GridRowSelectionModel
  ) => {
    let selectedRows: StockProjectionExceptionTableData[]
    if (selectionModel.type === 'include') {
      selectedRows = data.filter((row) => selectionModel.ids.has(row.index))
    } else {
      // 'exclude' type means "all rows except these" — used by Select All
      selectedRows = data.filter((row) => !selectionModel.ids.has(row.index))
    }
    handleRowClick(selectedRows)
  }

  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        headerName: 'Replenishment site',
        field: 'siteCode',
        flex: 1,
        type: 'string',
      },
      {
        headerName: 'SKU code',
        field: 'skuCode',
        flex: 1,
        type: 'string',
      },
      {
        headerName: 'SKU name',
        field: 'skuName',
        flex: 1,
        type: 'string',
      },
      {
        headerName: 'Replenishment type',
        field: 'replenishmentType',
        flex: 1,
        type: 'string',
      },
      {
        headerName: 'Cycle',
        field: 'cycleTime',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Next production week',
        field: 'nextProductionDateFormatted',
        flex: 1,
        type: 'string',
        valueFormatter: (value: string) => value ?? '-',
        valueGetter: (value: string) => value ?? '-',
      },
      {
        headerName: 'Stock projection exception week',
        field: 'dateKeyFormatted',
        flex: 1,
        type: 'string',
      },
      {
        headerName: 'Stock projection',
        field: 'minimumInventory',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Safety stock volume',
        field: 'safetyStockVolume',
        type: 'number',
        flex: 1,
        renderCell: (
          props: GridRenderCellParams<StockProjectionExceptionTableData, number>
        ) => props.value?.toLocaleString('en-US'),
      },
      {
        headerName: 'Stock difference',
        field: 'stockDifference',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Safety stock days',
        field: 'safetyStockDays',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Safety stock percent',
        field: 'safetyStockPercent',
        type: 'number',
        flex: 1,
        renderCell: (
          props: GridRenderCellParams<StockProjectionExceptionTableData, number>
        ) => {
          const originalRow: StockProjectionExceptionTableData = props.row

          let colorClass: ColorClassType = 'amber'
          if (originalRow.minimumInventory < 0) {
            colorClass = 'red'
          } else if (
            originalRow.minimumInventory >= 0 &&
            originalRow.minimumInventory < originalRow.safetyStockVolume
          ) {
            colorClass = 'amber'
          } else if (
            originalRow.minimumInventory >= 0 &&
            originalRow.minimumInventory >= originalRow.safetyStockVolume
          ) {
            colorClass = 'green'
          }
          const getBackgroundColor = (colorClass: ColorClassType) => {
            switch (colorClass) {
              case 'red':
                return '#ff0000'
              case 'green':
                return 'green'
              case 'amber':
                return '#FFBF00'
              default:
                return '#FFBF00'
            }
          }
          return (
            <Box display="flex" flexDirection="row" justifyContent="center">
              <Box
                sx={{
                  display: 'flex',
                  width: '80px',
                  height: '25px',
                  textAlign: 'center',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  color: (theme) => theme.palette.common.white,
                  borderRadius: '3px',
                  backgroundColor: getBackgroundColor(colorClass),
                }}
              >
                <Typography variant="button">{props.value}%</Typography>
              </Box>
            </Box>
          )
        },
      },
    ],
    []
  )

  const getRowId = (row: StockProjectionExceptionTableData) => {
    return row.index
  }

  // No-op functions for BaseTable required props (read-only table)
  const updateTableData = () => {
    // This is a read-only table, no updates needed
  }

  const uploadData = () => {
    // No upload functionality needed
  }

  // Custom toolbar to handle site filter changes
  const customToolbar = () => () => null

  // const [filterModel, setFilterModel] = useState<GridFilterModel>({
  //   items: [],
  // })

  //console.log('SPETable: Current filter model:', filterModel)
  // console.log('SPETable data:', data)
  return (
    <Box
      sx={{
        maxWidth: 'calc(100vw - 70px)',
      }}
    >
      <BaseTable
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={updateTableData}
        uploadData={uploadData}
        isUploading={false}
        isSaving={isLoading}
        checkBoxSelection={true}
        //showTableToolBar={true}
        handleRowClick={handleRowClickWrapper}
        onCheckboxSelectionChange={handleCheckboxSelectionChange}
        apiRefCallback={(ref) => {
          tableApiRef.current = ref
        }}
        downloadFileName="StockProjectionExceptions"
        getRowId={getRowId}
        showTopToolBar={false}
        customToolbar={customToolbar}
        getRowClassName={(params) =>
          hoveredGraphSite &&
          selectedSkuName &&
          params.row.siteCode === hoveredGraphSite &&
          params.row.skuName === selectedSkuName
            ? 'row--highlighted'
            : ''
        }
        sx={{
          '& .MuiDataGrid-columnHeaderCheckbox': {
            backgroundColor: 'white',
          },
          '& .MuiDataGrid-row.row--highlighted': {
            backgroundColor: 'rgba(0, 130, 179, 0.18)',
            boxShadow: 'inset 3px 0 0 0 #00415A',
            '& .MuiDataGrid-cell': {
              backgroundColor: 'inherit',
            },
          },
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
          // Cycle / Replenishment type are hidden until the COUPLED SKU data
          // gap (should read from the Production Plan Template) is fixed.
          columns: {
            columnVisibilityModel: {
              replenishmentType: false,
              cycleTime: false,
            },
          },
        }}
        //userDefinedFilterModel={filterModel}
        onFilterModelChange={(model: GridFilterModel) => {
          const { items } = model
          for (const item of items) {
            if (item.field === 'siteCode') {
              const { value } = item as { value: Set<string> }
              const sites =
                value &&
                typeof value === 'object' &&
                'size' in value &&
                value.size > 0
                  ? [...value]
                  : []
              handleSelectedGraphsChange(sites)
              break
            }
          }
          return model
        }}
      />
    </Box>
  )
}
